import { uploadMentorStoryFile } from "@/lib/api/mentor-stories";
import { signMediaUrl } from "@/lib/hooks/use-signed-media";
import {
  EMPTY_STORY_GRADIENT,
  STORY_DESIGN,
  STORY_KEYS,
  STORY_LAYOUT,
  STORY_META,
  STORY_STAGE,
  storyColor,
  wrapCaption,
  type StoryDecal,
  type StoryDraft,
  type StoryFrame,
  type StoryKey,
  type StorySlide,
} from "@/lib/mentor-stories/story-content";

const SCALE = 2;

const localPhotoUrls = new Map<string, string>();

export const rememberStoryPhoto = (key: string, file: Blob) => {
  const previous = localPhotoUrls.get(key);
  if (previous) URL.revokeObjectURL(previous);
  localPhotoUrls.set(key, URL.createObjectURL(file));
};

export const forgetStoryPhoto = (key: string) => {
  const url = localPhotoUrls.get(key);
  if (!url) return;
  URL.revokeObjectURL(url);
  localPhotoUrls.delete(key);
};

export const releaseStoryPhotos = () => {
  for (const url of localPhotoUrls.values()) URL.revokeObjectURL(url);
  localPhotoUrls.clear();
};

const loadImageElement = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load an image"));
    image.src = src;
  });

// The preview requests this URL with no CORS, and the cached response then fails the canvas check.
const loadImage = async (src: string) => {
  if (!/^https?:\/\//i.test(src)) return loadImageElement(src);
  const response = await fetch(src, { cache: "reload", mode: "cors" });
  if (!response.ok) throw new Error("Could not load an image");
  const blobUrl = URL.createObjectURL(await response.blob());
  try {
    return await loadImageElement(blobUrl);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
};

const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
};

const drawCover = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
) => {
  const scale = Math.max(width / image.width, height / image.height);
  const drawnWidth = image.width * scale;
  const drawnHeight = image.height * scale;
  ctx.drawImage(
    image,
    x + (width - drawnWidth) / 2,
    y + (height - drawnHeight) / 2,
    drawnWidth,
    drawnHeight,
  );
};

const drawDecal = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  decal: StoryDecal,
) => {
  ctx.drawImage(image, decal.x * SCALE, decal.y * SCALE, decal.w * SCALE, decal.h * SCALE);
};

const drawFrame = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  frame: StoryFrame,
) => {
  const x = frame.x * SCALE;
  const y = frame.y * SCALE;
  const width = frame.w * SCALE;
  const height = frame.h * SCALE;
  const radius = (frame.radius ?? 0) * SCALE;
  const border = (frame.border ?? 2) * SCALE;
  ctx.save();
  ctx.translate(x + width / 2, y + height / 2);
  ctx.rotate(((frame.rotate ?? 0) * Math.PI) / 180);
  ctx.translate(-width / 2, -height / 2);
  if (frame.hole) {
    const holeRadius = ((frame.hole - (frame.border ?? 2) * 2) / 2) * SCALE;
    ctx.save();
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, Math.PI * 2);
    ctx.arc(width / 2, height / 2, holeRadius, 0, Math.PI * 2, true);
    ctx.clip("evenodd");
    drawCover(ctx, image, 0, 0, width, height);
    ctx.restore();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = border;
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, Math.min(width, height) / 2 - border / 2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, (frame.hole * SCALE) / 2 - border / 2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    return;
  }
  roundRect(ctx, 0, 0, width, height, radius);
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.fill();
  ctx.save();
  roundRect(ctx, border, border, width - border * 2, height - border * 2, Math.max(0, radius - border));
  ctx.clip();
  drawCover(ctx, image, border, border, width - border * 2, height - border * 2);
  ctx.restore();
  ctx.lineWidth = border;
  ctx.strokeStyle = "#ffffff";
  roundRect(ctx, border / 2, border / 2, width - border, height - border, Math.max(0, radius - border / 2));
  ctx.stroke();
  ctx.restore();
};

const drawStory = async (draft: StoryDraft, key: StoryKey) => {
  const canvas = document.createElement("canvas");
  canvas.width = STORY_STAGE.width * SCALE;
  canvas.height = STORY_STAGE.height * SCALE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not render the story");

  const section = draft[key];
  const color = storyColor(section.color) ?? EMPTY_STORY_GRADIENT;
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, color.from);
  gradient.addColorStop(0.22, color.from);
  gradient.addColorStop(1, color.to);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const fit = STORY_STAGE.width / STORY_DESIGN.width;
  ctx.scale(fit, fit);

  const layout = STORY_LAYOUT[key];
  const offset = layout.offsetY;
  const [behind, frames, front] = await Promise.all([
    Promise.all(layout.behind.map((decal) => loadImage(decal.src))),
    Promise.all(
      layout.frames.map(async (frame) => {
        const photo = section.photos.find((item) => item.slot === frame.slot);
        if (!photo?.url) throw new Error("Could not render the story");
        const local = localPhotoUrls.get(photo.key);
        return loadImage(local ?? (await signMediaUrl(photo.url)));
      }),
    ),
    Promise.all(layout.front.map((decal) => loadImage(decal.src))),
  ]);

  behind.forEach((image, index) => {
    const decal = layout.behind[index];
    if (decal) drawDecal(ctx, image, { ...decal, y: decal.y + offset });
  });

  const meta = STORY_META[key];
  const textTop = 86 + offset;
  const alignStart = layout.align === "start";
  ctx.fillStyle = "#1e1e1e";
  ctx.textAlign = alignStart ? "left" : "center";
  const textX = (alignStart ? 22 : STORY_DESIGN.width / 2) * SCALE;
  ctx.font = `${12 * SCALE}px ui-monospace, monospace`;
  ctx.fillText(meta.eyebrow, textX, (textTop + 12) * SCALE);
  const caption = section.caption?.trim();
  if (caption) {
    const captionSize = layout.captionSize;
    ctx.font = `bold ${captionSize * SCALE}px sans-serif`;
    const lines = wrapCaption(
      caption.toLowerCase(),
      (STORY_DESIGN.width - 44) * SCALE,
      (value) => ctx.measureText(value).width,
    );
    const lineHeight = captionSize * SCALE;
    const firstBaseline = (textTop + 12 + 5 + captionSize * 0.82) * SCALE;
    lines.forEach((line, index) => {
      ctx.fillText(line, textX, firstBaseline + index * lineHeight);
    });
  }

  frames.forEach((image, index) => {
    const frame = layout.frames[index];
    if (frame) drawFrame(ctx, image, { ...frame, y: frame.y + offset });
  });
  front.forEach((image, index) => {
    const decal = layout.front[index];
    if (decal) drawDecal(ctx, image, { ...decal, y: decal.y + offset });
  });

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (value) => (value ? resolve(value) : reject(new Error("Could not render the story"))),
      "image/jpeg",
      0.9,
    );
  });
};

export const uploadRenderedSlides = async (
  draft: StoryDraft,
): Promise<StorySlide[]> =>
  Promise.all(
    STORY_KEYS.map(async (key, order) => {
      const blob = await drawStory(draft, key);
      const uploaded = await uploadMentorStoryFile(
        new File([blob], `${key}.jpg`, { type: "image/jpeg" }),
      );
      return {
        eyebrow: STORY_META[key].eyebrow,
        header: draft[key].caption?.trim() || null,
        key: uploaded.key,
        order,
        url: uploaded.url,
      };
    }),
  );
